import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { X, Check, Euro, TrendingUp, Users, Database } from 'lucide-react';
import { getImageAssetPath } from '@/lib/imageHelper';
import OptimizedImage from '@/components/common/OptimizedImage';

const TransformationSection = () => {
  const { t } = useTranslation();
  const profileImagePath = getImageAssetPath('ddi.jpeg');
  
  return (
    <section id="transformation" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Du glaubst, du kannst dir eine maßgeschneiderte Lösung nicht leisten?
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Wir können bereits ab einem Budget von 20.000 € oder 1.600 € pro Monat individuelle Software entwickeln, die dein Unternehmen weiterbringt.
          </p>
        </div>
        
        {/* Beispiel 1: SaaS-CRM vs. Maßgeschneiderte Lösung */}
        <div className="max-w-6xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
            Beispiel 1: SaaS-CRM vs. Maßgeschneiderte Lösung
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Standard SaaS-CRM */}
            <div className="bg-red-50 rounded-xl overflow-hidden border border-red-100">
              <div className="bg-red-600 text-white p-4 text-center">
                <h4 className="text-xl font-bold flex items-center justify-center">
                  <X className="h-6 w-6 mr-2" />
                  Standard SaaS-CRM
                </h4>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Monatliche Kosten:</span>
                    <span className="text-red-600 font-bold">150 € × 10 User = 1.500 €/Monat</span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold">Jährliche Kosten:</span>
                    <span className="text-red-600 font-bold">18.000 €</span>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Viele ungenutzte Features, die du bezahlst</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Keine Integration in dein ERP-System</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Doppelte Datenpflege notwendig</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Anpassung deiner Prozesse an die Software</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Jährliche Preiserhöhungen</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Abhängigkeit vom Anbieter</span>
                  </li>
                </ul>
                
                <div className="mt-4 p-3 bg-red-100 rounded-lg">
                  <p className="text-red-800 font-semibold text-sm">
                    Nach 3 Jahren: 54.000 € + versteckte Kosten durch Ineffizienz
                  </p>
                </div>
              </div>
            </div>
            
            {/* Maßgeschneiderte Lösung */}
            <div className="bg-green-50 rounded-xl overflow-hidden border border-green-100 shadow-md">
              <div className="bg-green-600 text-white p-4 text-center">
                <h4 className="text-xl font-bold flex items-center justify-center">
                  <Check className="h-6 w-6 mr-2" />
                  Maßgeschneiderte CRM-Lösung
                </h4>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Einmalige Investition:</span>
                    <span className="text-green-600 font-bold">25.000 €</span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold">Hosting & Wartung:</span>
                    <span className="text-green-600 font-bold">200 €/Monat</span>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Nur die Features, die du wirklich brauchst</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Nahtlose ERP-Integration</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Automatischer Datenabgleich</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Software passt sich deinen Prozessen an</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Keine laufenden Lizenzkosten</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Vollständige Kontrolle und Eigentum</span>
                  </li>
                </ul>
                
                <div className="mt-4 p-3 bg-green-100 rounded-lg">
                  <p className="text-green-800 font-semibold text-sm">
                    Nach 3 Jahren: 32.200 € + massive Effizienzgewinne
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Beispiel 2: Produktdatenverwaltung */}
        <div className="max-w-6xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">
            Beispiel 2: Standard-Produktdatenverwaltung vs. Maßgeschneiderte Lösung
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Standard Produktdatenverwaltung */}
            <div className="bg-red-50 rounded-xl overflow-hidden border border-red-100">
              <div className="bg-red-600 text-white p-4 text-center">
                <h4 className="text-xl font-bold flex items-center justify-center">
                  <X className="h-6 w-6 mr-2" />
                  Standard-Produktdatenverwaltung
                </h4>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Software-Lizenz:</span>
                    <span className="text-red-600 font-bold">600 €/Monat</span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold">Jährliche Kosten:</span>
                    <span className="text-red-600 font-bold">7.200 €</span>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Komplexe Datenstrukturen für einfache Produkte</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Keine Anbindung an Online-Shop</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Manuelle Produktdaten-Synchronisation</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Umständliche Bildverwaltung</span>
                  </li>
                  <li className="flex items-start">
                    <X className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Keine automatischen Preiskalkulationen</span>
                  </li>
                </ul>
                
                <div className="mt-4 p-3 bg-red-100 rounded-lg">
                  <p className="text-red-800 font-semibold text-sm">
                    Nach 3 Jahren: 21.600 € + hoher Pflegeaufwand
                  </p>
                </div>
              </div>
            </div>
            
            {/* Maßgeschneiderte Produktdatenverwaltung */}
            <div className="bg-green-50 rounded-xl overflow-hidden border border-green-100 shadow-md">
              <div className="bg-green-600 text-white p-4 text-center">
                <h4 className="text-xl font-bold flex items-center justify-center">
                  <Check className="h-6 w-6 mr-2" />
                  Maßgeschneiderte Produktdatenverwaltung
                </h4>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Einmalige Entwicklung:</span>
                    <span className="text-green-600 font-bold">18.000 €</span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold">Hosting & Updates:</span>
                    <span className="text-green-600 font-bold">120 €/Monat</span>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Einfache Produkterfassung für deine Bedürfnisse</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Direkte Shop-Integration</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Automatische Datenverteilung</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Intelligente Bildverwaltung mit Komprimierung</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Automatische Preisberechnung nach Regeln</span>
                  </li>
                </ul>
                
                <div className="mt-4 p-3 bg-green-100 rounded-lg">
                  <p className="text-green-800 font-semibold text-sm">
                    Nach 3 Jahren: 22.320 € + minimaler Pflegeaufwand
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Emotionales Zitat */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-blue-100 rounded-xl p-6 md:p-8 relative">
            <div className="text-6xl text-blue-300 font-serif absolute top-4 left-4">"</div>
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="md:w-1/4 flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white shadow-md">
                  <OptimizedImage 
                    src={profileImagePath} 
                    alt="Dennis Dittrich, splusd-solutions" 
                    width={128}
                    height={128}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="md:w-3/4">
                <blockquote className="relative z-10">
                  <p className="text-lg md:text-xl text-blue-900 italic mb-6">
                    "Software ist dafür da, deine bewährten Abläufe zu stärken – nicht, sie zu diktieren.
                    Unternehmen, die ihre Prozesse über Jahre erfolgreich verfeinert haben, brauchen keine Revolution – sie brauchen eine Software, die sich nahtlos anpasst, statt alles auf den Kopf zu stellen."
                  </p>
                  <footer className="text-blue-800 font-medium">
                    Dennis Dittrich, Geschäftsführer, splusd-solutions
                  </footer>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationSection; 